import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Save, Plus, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import ModalWrapper, { ModalCard } from '../ModalWrapper';
import UndoRedoButtons from '../UndoRedoButtons';
import useTextHistory from '../../hooks/useTextHistory';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import AIButton from '../AIButton';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
`;


const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: rgba(76, 175, 80, 0.6);
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid ${props => props.$aiGenerated
    ? 'rgba(168, 85, 247, 0.3)'
    : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 10px;
  color: #fff;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.15s ease;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  line-height: 1.5;

  &:focus {
    border-color: rgba(76, 175, 80, 0.6);
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const AIGenerateBtn = styled(AIButton)`
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 3px 8px;
  gap: 4px;
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

const AIHint = styled.div`
  font-size: 0.7rem;
  color: rgba(168, 85, 247, 0.6);
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
`;

const CancelButton = styled(Button)`
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.12);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`;

const SaveButton = styled(Button)`
  background: #4CAF50;
  color: white;

  &:hover {
    background: #43A047;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/**
 * PivotPointEditModal - Edit or create a pivot point.
 * In create mode, offers AI description generation from the title.
 */
export default function PivotPointEditModal({
  mode = 'edit',
  pivotPoint,
  currentTitle,
  currentDescription,
  instanceTitle,
  instanceId,
  onSave,
  onClose,
}) {
  const { t } = useTranslation();
  const isCreate = mode === 'create';
  const [title, setTitle] = useState(isCreate ? '' : (currentTitle || pivotPoint?.title || ''));
  const [description, setDescription] = useState(isCreate ? '' : (currentDescription || pivotPoint?.description || ''));
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const inputRef = useRef(null);
  const {
    canUndo,
    canRedo,
    pushSnapshot,
    undo,
    redo,
    setCurrentValue,
  } = useTextHistory(setDescription);

  useEffect(() => {
    inputRef.current?.focus();
    if (!isCreate) inputRef.current?.select();
  }, [isCreate]);

  useEffect(() => {
    setCurrentValue(description);
  }, [description, setCurrentValue]);

  const generateDescription = useCallback(async () => {
    if (!title.trim() || aiLoading) return;
    pushSnapshot(description);
    setAiLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.AI_PIVOT_DESCRIPTION(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The instance gates the AI call and picks the prompt's language (#1408).
        body: JSON.stringify({ title: title.trim(), instanceTitle, instanceId }),
      });
      if (response.ok) {
        const data = await response.json();
        setDescription(data.description);
        setAiGenerated(true);
      }
    } catch (err) {
      console.error('AI description generation failed:', err);
    } finally {
      setAiLoading(false);
    }
  }, [title, instanceTitle, instanceId, aiLoading, description, pushSnapshot]);

  const origDesc = currentDescription || pivotPoint?.description || '';
  const titleChanged = title.trim() !== (currentTitle || pivotPoint?.title || '');
  const descChanged = description.trim() !== origDesc;
  const canSave = isCreate
    ? title.trim().length > 0
    : title.trim().length > 0 && (titleChanged || descChanged);

  const handleSave = async () => {
    if (aiLoading) return;

    // Auto-generate description if empty — stay in modal so user can review
    if (!description.trim() && title.trim()) {
      await generateDescription();
      return; // Don't close — user will see the generated text and save again
    }

    if (!canSave) return;
    onSave({ title: title.trim(), description: description.trim() });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <ModalWrapper visible onClose={onClose} zIndex={1000} opacity={0.85}>
      <ModalCard size="sm" onClick={(e) => e.stopPropagation()} style={{ padding: '28px 32px 32px' }}>
        <Header>
          <Title>{isCreate ? t('lenses.editModal.createTitle') : t('lenses.editModal.editTitle')}</Title>
        </Header>

        <FieldGroup>
          <LabelRow>
            <Label>{t('lenses.editModal.titleLabel')}</Label>
          </LabelRow>
          <Input
            ref={inputRef}
            value={title}
            onChange={(e) => { setTitle(e.target.value); setAiGenerated(false); }}
            onKeyDown={handleKeyDown}
            placeholder={t('lenses.editModal.titlePlaceholder')}
          />
        </FieldGroup>

        <FieldGroup>
          <LabelRow>
            <Label>{t('lenses.editModal.descriptionLabel')}</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <UndoRedoButtons
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={undo}
                onRedo={redo}
                buttonComponent={AIGenerateBtn}
                buttonStyle={{ padding: '3px 8px' }}
                dividerStyle={{ margin: '0 2px' }}
              />
            <AIGenerateBtn
              onClick={generateDescription}
              disabled={!title.trim() || aiLoading}
              title={aiGenerated ? t('lenses.editModal.replaceTitle') : t('lenses.editModal.generateTitle')}
              $warning={aiGenerated}
            >
              {aiLoading ? <Spinner size={11} /> : aiGenerated ? <AlertTriangle size={11} /> : <Sparkles size={11} />}
              {aiLoading ? t('lenses.editModal.generating') : aiGenerated ? t('lenses.editModal.regenerate') : t('lenses.editModal.generateAi')}
            </AIGenerateBtn>
            </div>
          </LabelRow>
          <Textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); setAiGenerated(false); }}
            onKeyDown={handleKeyDown}
            placeholder={isCreate ? t('lenses.editModal.descriptionPlaceholderCreate') : t('lenses.editModal.descriptionPlaceholderEdit')}
            $aiGenerated={aiGenerated}
          />
          {aiGenerated && (
            <AIHint>
              <Sparkles size={10} />
              {t('lenses.editModal.aiHint')}
            </AIHint>
          )}
        </FieldGroup>

        <ButtonRow>
          <CancelButton onClick={onClose}>{t('common.cancel')}</CancelButton>
          <SaveButton onClick={handleSave} disabled={!canSave}>
            {isCreate ? <Plus size={16} /> : <Save size={16} />}
            {isCreate ? t('common.add') : t('common.save')}
          </SaveButton>
        </ButtonRow>
      </ModalCard>
    </ModalWrapper>
  );
}
